import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  email as rule_email,
  min as rule_min,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  max as rule_max,
  max_value as rule_max_value,
  min_value as rule_min_value,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
} from 'vee-validate/dist/rules'
import pt from 'vee-validate/dist/locale/pt_BR.json'
import en from 'vee-validate/dist/locale/en.json'
import { cpf, cnpj } from './cpf_cnpj/cpf_cnpj_validator'

import VueI18n from "vue-i18n";

const i18n = new VueI18n({
  locale: "pt", // TODO change to get the current locale
  messages: {
    pt: {
      validation: pt.messages
    }
  }
});

// eslint-disable-next-line object-curly-newline
import { validatorPositive, validatorUrlValidator, validatorPassword, validatorCreditCard } from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

// export const required = extend('required', rule_required)

export const required = extend("required", {
  ...rule_required,
  message: (_, values) => i18n.t('validation.required', values)
})

export const email = extend("email", {
  ...rule_email,
  message: (_, values) => i18n.t('validation.email', values)
})

export const min = extend('min', {...rule_min,  message: (_, values) => i18n.t('validation.min', values)})

export const max = extend('max', {...rule_max, message: (_, values) => i18n.t('validation.max', values)})

export const max_value = extend('max_value', {...rule_max_value, message: (_, values) => i18n.t('validation.max_value', values)})

export const min_value = extend('min_value', {...rule_min_value, message: (_, values) => i18n.t('validation.min_value', values)})

export const confirmed = extend('confirmed', rule_confirmed)

/**
 * Idade mínima (LGPD, Art. 14) — `rules="required|min_age"` nos formulários que
 * criam conta: aluno, professor, profissional e o cadastro público.
 *
 * Espelha `privacy.min_age` do backend, que é quem decide de verdade. Aqui é para
 * a pessoa saber antes de preencher o formulário inteiro e levar 422 no fim.
 *
 * O cálculo respeita se o aniversário já passou — subtrair só o ano dá 18 para
 * quem nasceu em 31/12 assim que vira janeiro.
 */
export const MIN_AGE = 18

export const min_age = extend('min_age', {
  validate(value, args) {
    if (!value) return true

    const minAge = Number((args && args.length ? args[0] : MIN_AGE))
    const birthDate = value instanceof Date ? value : new Date(value)

    if (isNaN(birthDate.getTime()) || birthDate > new Date()) return false

    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1

    return age >= minAge
  },
  message: () =>
    `É necessário ter pelo menos ${MIN_AGE} anos. Menores de idade participam das atividades matriculados por um responsável legal.`
})

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

export const positive = extend('positive', {
  validate: validatorPositive,
  message: 'Please enter positive number!',
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: 'It is not valid credit card!',
})

export const password = extend('password', {
  validate: validatorPassword,
  message: 'O campo {_field_} deve conter ao menos uma letra maiúscula, uma letra minúscula, um caractere especial e um digito.',
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: 'URL is invalid',
})

export const vcpf = extend('cpf', cpf)
export const vcnpj = extend('cnpj', cnpj)

// Install English and Portuguese localizations.
localize({
  pt: {
    messages: pt.messages,
    names: {
      email: 'Email',
      password: 'Senha',
    },
    fields: {
      password: {
        min: '{_field_} is too short, you want to get hacked?',
      },
    },
  },
  en: {
    messages: en.messages,
    names: {
      email: 'Email',
      password: 'Password',
    },
    fields: {
      password: {
        min: '{_field_} is too short, you want to get hacked?',
      },
    },
  },
})
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
